// client/src/utils/accessToken.ts
//
// Synchronous accessor for the auth token used as `Authorization: Bearer ...`
// on API calls. Replaces `Cookies.get('token')` reads that previously walked
// the JS-readable `token` cookie (now retired).
//
// Two sources of truth, picked in order:
//   1. Supabase session access token (OAuth callback, setSession).
//      Auto-refreshed by the SDK; cached here so synchronous callers
//      (axios interceptor, etc.) don't need to await getSession().
//   2. Custom JWT issued by /api/auth/email/signin and
//      /api/auth/email/signup. Set via setCustomToken() from the
//      AuthContext right after signin succeeds. Cleared on signout.
//
// The Supabase session takes precedence because it's validated and
// auto-refreshed server-side; the custom JWT is what's available for
// email/password flows that never hit the Supabase auth session.
//
// The HttpOnly `auth_session` cookie set by the server is what server
// middleware reads for auth - that's separate from this helper, which is
// only for outgoing client requests.
//
// DEL-771: the Supabase client is loaded lazily via `getSupabase()` so the
// @supabase/ssr SDK chunk only lands in the browser when something
// actually triggers the OAuth-path initialisation. Email/password users
// still get a Bearer token via `setCustomToken()` without ever loading
// the SDK.
//
// DEL-892: `getAccessToken()` is called from API-request code paths that
// fire on /dashboard mount. If we eagerly kick off `initialize()` for
// every caller we end up loading the SDK even for unauthenticated
// visitors who only ever use the email HttpOnly-cookie flow. Gate the
// SDK initialisation on a localStorage probe so the chunk only lands
// when a Supabase session is actually present in the browser.

import { getSupabase } from '@/supabaseClient';

let cachedSupabaseToken: string | null = null;
let cachedCustomToken: string | null = null;
let initPromise: Promise<void> | null = null;

function initialize(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (initPromise) return initPromise;

  // DEL-892: skip the SDK load when no Supabase session is in browser
  // storage. The HTTP-only cookie email flow uses cachedCustomToken and
  // never needs the SDK; this avoids pulling the @supabase/ssr chunk into
  // /dashboard for unauthenticated visitors that just happen to call
  // getAccessToken() from a /api request interceptor.
  let hasSupabaseSession = false;
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
        hasSupabaseSession = true;
        break;
      }
    }
  } catch {
    // localStorage can throw in private modes. Treat as no session.
  }
  if (!hasSupabaseSession) {
    initPromise = Promise.resolve();
    return initPromise;
  }

  initPromise = (async () => {
    const supabase: any = await getSupabase();
    supabase.auth.onAuthStateChange((_event: any, session: any) => {
      cachedSupabaseToken = session?.access_token ?? null;
    });

    const { data } = await supabase.auth.getSession();
    cachedSupabaseToken = data.session?.access_token ?? null;
  })();

  return initPromise;
}

export function getAccessToken(): string | null {
  // Kick off the lazy init. We don't await - the OAuth bearer shows up
  // synchronously for the common path (the next `getAccessToken()` after
  // init resolves will pick it up), and email/password flows rely on
  // `cachedCustomToken` which is populated synchronously by AuthContext.
  void initialize();
  return cachedSupabaseToken ?? cachedCustomToken;
}

export function setCustomToken(token: string | null): void {
  cachedCustomToken = token;
}

export function clearCachedAccessToken(): void {
  cachedSupabaseToken = null;
  cachedCustomToken = null;
}